import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Search, Download, CheckCircle2 } from "lucide-react";

const EXPORTS = [["contacts", "Contatti"], ["deals", "Trattative"], ["obligations", "Pagamenti previsti"], ["sales", "Vendite"], ["projects", "Progetti"]];

function Card({ title, children, tid }) {
  return <div className="card-clean p-5" data-testid={tid}><div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{title}</div>{children}</div>;
}

function ResultCol({ title, items, label, onOpen, kind }) {
  return (
    <div><div className="text-xs text-neutral-500 mb-1">{title} ({items.length})</div>
      {items.map((x) => <button key={x.id} onClick={() => onOpen(kind, x.id)} data-testid={`search-result-${kind}-${x.id}`} className="block w-full text-left truncate underline decoration-dotted hover:text-[color:var(--brand-primary)]">{label(x)}</button>)}
    </div>
  );
}

const SEARCH_PATHS = { contacts: "contatti", deals: "trattative", projects: "progetti", agents: "venditori" };

export default function CrmReport() {
  const navigate = useNavigate();
  const [comm, setComm] = useState(null);
  const [eco, setEco] = useState(null);
  const [ops, setOps] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [dups, setDups] = useState(null);
  const [react, setReact] = useState(null);
  const [agents, setAgents] = useState([]);
  const [approve, setApprove] = useState({ agent_id: "", month: new Date().toISOString().slice(0, 7) });
  const [coop, setCoop] = useState([]);
  const [coopForm, setCoopForm] = useState({ kind: "comunicazione", subject: "", reference: "", note: "" });
  const [tech, setTech] = useState(false);

  const loadCoop = async () => { try { const { data } = await http.get("/crm/coop-communications"); setCoop(data); } catch (e) { /* noop */ } };

  const load = async () => {
    try {
      const [c, e, o, d, r, a] = await Promise.all([
        http.get("/crm/reports/commercial"), http.get("/crm/reports/economic"), http.get("/crm/reports/operational"),
        http.get("/crm/contacts/duplicates"), http.get("/crm/contacts/to-reactivate"), http.get("/crm/agents", { params: { status: "attivo" } }),
      ]);
      setComm(c.data); setEco(e.data); setOps(o.data); setDups(d.data); setReact(r.data); setAgents(a.data);
    } catch (err) { toast.error(formatErr(err)); }
  };
  useEffect(() => { load(); loadCoop(); /* eslint-disable-next-line */ }, []);

  const search = async () => {
    if (!q.trim()) return;
    try { const { data } = await http.get("/crm/search", { params: { q } }); setResults(data); } catch (e) { toast.error(formatErr(e)); }
  };

  const exportCsv = async (entity) => {
    try {
      const res = await http.get(`/crm/export/${entity}`, { responseType: "blob", params: { formato: tech ? "tecnico" : "leggibile" } });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${entity}${tech ? "" : "_leggibile"}.csv`; a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doApprove = async () => {
    if (!approve.agent_id) return toast.error("Seleziona un agente.");
    if (!window.confirm(`Approvare il prospetto ${approve.month.split("-").reverse().join("/")} per la fatturazione?`)) return;
    try { const { data } = await http.post("/crm/commissions/approve", approve); toast.success(`Prospetto approvato per fatturazione (versione ${data.version}).`); } catch (e) { toast.error(formatErr(e)); }
  };

  const createCoop = async () => {
    if (!coopForm.subject.trim()) return toast.error("Oggetto della comunicazione richiesto.");
    try { await http.post("/crm/coop-communications", coopForm); setCoopForm({ kind: "comunicazione", subject: "", reference: "", note: "" }); await loadCoop(); toast.success("Comunicazione registrata (da inviare)."); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const markSentCoop = async (id) => {
    try { await http.post(`/crm/coop-communications/${id}/mark-sent`); await loadCoop(); toast.success("Registrata come inviata."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const openResult = (kind, id) => navigate(`/platform/crm/${SEARCH_PATHS[kind]}?open=${id}`);

  return (
    <div data-testid="crm-report-page" className="max-w-6xl mx-auto space-y-5">
      <h1 className="font-serif text-3xl">Report e strumenti</h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Commerciale" tid="report-commercial">
          <div className="text-sm">Trattative: <strong>{comm?.total_deals ?? "—"}</strong></div>
          <div className="text-sm">Vinte: <strong>{comm?.won ?? "—"}</strong></div>
          <div className="text-sm">Conversione: <strong>{comm ? `${(comm.conversion_rate * 100).toFixed(1)}%` : "—"}</strong></div>
          <div className="text-sm">Offerte: <strong>{comm?.offers ?? "—"}</strong></div>
        </Card>
        <Card title="Economico" tid="report-economic">
          <div className="text-sm">Pagamenti previsti: <strong>{eco?.obligations_count ?? "—"}</strong></div>
          <div className="text-sm">Lordo: <strong>€ {eco?.gross_amount ?? "—"}</strong></div>
          <div className="text-sm">Provvigioni liquidate: <strong>€ {eco?.commissions_liquidated ?? "—"}</strong></div>
        </Card>
        <Card title="Operativo" tid="report-operational">
          <div className="text-sm">Progetti: <strong>{ops?.projects ?? "—"}</strong></div>
          <div className="text-sm">Ticket aperti: <strong>{ops?.open_tickets ?? "—"}</strong></div>
          <div className="text-sm">Agenti attivi: <strong>{ops?.active_agents ?? "—"}</strong></div>
        </Card>
      </div>

      <Card title="Ricerca globale" tid="report-search">
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Cerca contatti, trattative, progetti, agenti…" data-testid="report-search-input" className="flex-1 px-3 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }} />
          <button onClick={search} data-testid="report-search-btn" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-1" style={{ background: "var(--brand-dark)" }}><Search className="w-4 h-4" /> Cerca</button>
        </div>
        <p className="text-[11px] text-neutral-400 mt-2">Scrivi un nome, un'email, un telefono o una partita IVA e premi Invio o "Cerca" (nessun suggerimento automatico durante la digitazione). Clicca un risultato per aprirne la scheda.</p>
        {results && (
          <div className="mt-3 text-sm grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="report-search-results">
            {Object.values(results).every((v) => !v.length) && <div className="md:col-span-4 text-neutral-500" data-testid="report-search-empty">Nessun risultato per "{q}".</div>}
            <ResultCol title="Contatti" kind="contacts" items={results.contacts} label={(c) => c.commercial_name} onOpen={openResult} />
            <ResultCol title="Trattative" kind="deals" items={results.deals} label={(d) => d.title} onOpen={openResult} />
            <ResultCol title="Progetti" kind="projects" items={results.projects} label={(p) => p.salon_name} onOpen={openResult} />
            <ResultCol title="Agenti" kind="agents" items={results.agents} label={(a) => [a.profile?.first_name, a.profile?.last_name].filter(Boolean).join(" ") || a.email} onOpen={openResult} />
          </div>
        )}
      </Card>

      <Card title="Esporta CSV" tid="report-export">
        <div className="flex flex-wrap gap-2 items-center">
          {EXPORTS.map(([e, l]) => (
            <button key={e} onClick={() => exportCsv(e)} data-testid={`report-export-${e}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}><Download className="w-3 h-3" /> {l}</button>
          ))}
          <label className="flex items-center gap-1 text-xs text-neutral-500 ml-2"><input type="checkbox" checked={tech} onChange={(e) => setTech(e.target.checked)} data-testid="report-export-tech" /> Formato tecnico (per integrazioni)</label>
        </div>
        <p className="text-[11px] text-neutral-400 mt-2">Formato leggibile: intestazioni in italiano, date gg/mm/aaaa e orari HH:MM (Europe/Rome), separatore ";" per Excel. Il formato tecnico mantiene chiavi e date ISO originali.</p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Duplicati contatti" tid="report-duplicates">
          {dups?.count ? dups.duplicate_groups.map((g) => (
            <div key={g.key} className="text-sm mb-1">{g.key} — {g.entries.length} voci</div>
          )) : <div className="text-sm text-neutral-500">Nessun duplicato.</div>}
        </Card>
        <Card title={`Da riattivare (${react?.reactivation_days ?? "—"} gg)`} tid="report-reactivate">
          {react?.count ? react.contacts.slice(0, 10).map((c) => (
            <div key={c.id} className="text-sm">{c.commercial_name} <span className="text-neutral-400">· {c.days_inactive} gg</span></div>
          )) : <div className="text-sm text-neutral-500">Nessun contatto da riattivare.</div>}
        </Card>
      </div>

      <Card title="Approvazione prospetto per fatturazione" tid="report-approve">
        <div className="flex gap-2 flex-wrap items-end">
          <label className="block text-sm"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Agente</span>
            <select value={approve.agent_id} onChange={(e) => setApprove({ ...approve, agent_id: e.target.value })} data-testid="approve-agent" className="px-2 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }}><option value="">{agents.length ? "— Seleziona agente —" : "Nessun agente attivo"}</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.profile?.first_name || a.email}</option>)}</select></label>
          <label className="block text-sm"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Mese</span>
            <input type="month" value={approve.month} onChange={(e) => setApprove({ ...approve, month: e.target.value })} data-testid="approve-month" className="px-2 py-2 rounded-md border text-sm" style={{ borderColor: "var(--border-silver)" }} /></label>
          <button onClick={doApprove} disabled={!approve.agent_id} data-testid="approve-btn" className="px-4 py-2 rounded-md text-white text-sm flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed" style={{ background: "var(--brand-primary)" }}><CheckCircle2 className="w-4 h-4" /> Approva prospetto</button>
        </div>
        {!approve.agent_id && <p className="text-[11px] text-neutral-400 mt-2" data-testid="approve-hint">{agents.length ? "Seleziona l'agente e il mese del prospetto da approvare." : "Nessun agente attivo: non ci sono prospetti da approvare."}</p>}
      </Card>

      <Card title="Comunicazioni cooperativa (registrazione manuale)" tid="report-coop">
        <p className="text-[11px] text-neutral-400 mb-3">Registro interno delle comunicazioni verso la cooperativa. La registrazione NON invia nulla: traccia solo cosa è da comunicare e cosa è stato comunicato.</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-sm mb-3">
          <select value={coopForm.kind} onChange={(e) => setCoopForm({ ...coopForm, kind: e.target.value })} data-testid="coop-kind" className="px-2 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }}>
            {["comunicazione", "incasso", "fatturazione", "altro"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input value={coopForm.subject} onChange={(e) => setCoopForm({ ...coopForm, subject: e.target.value })} placeholder="Oggetto *" data-testid="coop-subject" className="px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
          <input value={coopForm.reference} onChange={(e) => setCoopForm({ ...coopForm, reference: e.target.value })} placeholder="Riferimento pratica (opz.)" data-testid="coop-reference" className="px-3 py-2 rounded-md border" style={{ borderColor: "var(--border-silver)" }} />
          <button onClick={createCoop} data-testid="coop-create" className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-dark)" }}>Registra</button>
        </div>
        <div data-testid="coop-list">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Da inviare ({coop.filter((c) => c.status === "da_inviare").length})</div>
          {coop.length === 0 ? <p className="text-sm text-neutral-500">Nessuna comunicazione registrata.</p> : coop.map((c) => (
            <div key={c.id} className="flex items-center justify-between text-sm py-2 border-b last:border-0" style={{ borderColor: "var(--border-silver)" }} data-testid={`coop-row-${c.id}`}>
              <span><b className="capitalize">{c.kind}</b> — {c.subject}{c.reference ? ` · rif. ${c.reference}` : ""} <span className={`text-xs ml-2 px-2 py-0.5 rounded ${c.status === "inviata" ? "text-green-700" : "text-amber-700"}`} style={{ background: "var(--bg-main)" }}>{c.status === "inviata" ? "inviata" : "da inviare"}</span></span>
              {c.status !== "inviata" && <button onClick={() => markSentCoop(c.id)} data-testid={`coop-mark-sent-${c.id}`} className="px-3 py-1 rounded border text-xs" style={{ borderColor: "var(--border-silver)" }}>Segna inviata</button>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
